import {
  Cake,
  Clock,
  CreditCard,
  Mail,
  MailCheck,
  MapPin,
  MessageSquare,
  Phone,
  Stethoscope,
  RotateCcw,
  Timer,
  TriangleAlert,
  User,
  Video,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  AppointmentChargeStatus,
  ApprovalFeeOutcome,
  getApprovalFeeOutcomeColor,
  getApprovalFeeOutcomeLabel,
  getPaymentStatusLabel,
  getVisitTypeLabel,
} from "@/api/enums";
import { formatCents } from "@/utils/cancellation-policy";
import {
  formatAppointmentTime as formatTimeSlot,
  formatDisplayDate,
  formatLongDate,
  formatUtcTime,
} from "@/utils/date";
import type { AppointmentResponse } from "@/api/contracts/appointments.contract";

const InfoRow = ({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-start gap-3">
    <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
        {label}
      </p>
      {children}
    </div>
  </div>
);

export function AppointmentDetailsCard({
  appointment,
}: {
  appointment: AppointmentResponse;
}) {
  const visitTypeLabel = getVisitTypeLabel(appointment.visitType);
  const paymentStatusLabel = getPaymentStatusLabel(appointment.paymentStatus);
  const hasMeaningfulPaymentStatus =
    paymentStatusLabel && paymentStatusLabel.toLowerCase() !== "none";

  return (
    <Card>
      <CardHeader className="p-4 pb-2 lg:p-6 lg:pb-3">
        <CardTitle className="text-base font-semibold">
          Appointment Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0 lg:p-6 lg:pt-0">
        <div className="rounded-lg border border-border/70 bg-muted/30 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Scheduled for
          </p>
          <p className="text-sm font-semibold text-foreground">
            {formatDisplayDate(appointment.startTime)} at{" "}
            {formatTimeSlot(appointment.startTime)}
          </p>
        </div>
        {appointment.requestedTime && (
          <InfoRow icon={Clock} label="Request Created">
            <p className="text-sm font-medium text-foreground">
              {formatDisplayDate(appointment.requestedTime)} at{" "}
              {formatUtcTime(appointment.requestedTime)}
            </p>
          </InfoRow>
        )}
        <InfoRow icon={Video} label="Visit Type">
          <p className="text-sm font-medium text-foreground">
            {visitTypeLabel}
          </p>
        </InfoRow>
        {appointment.durationInMinutes > 0 && (
          <InfoRow icon={Timer} label="Duration">
            <p className="text-sm font-medium text-foreground">
              {appointment.durationInMinutes} min
            </p>
          </InfoRow>
        )}
        {hasMeaningfulPaymentStatus && (
          <InfoRow icon={CreditCard} label="Payment Status">
            <Badge variant="outline" className="text-xs font-normal">
              {paymentStatusLabel}
            </Badge>
          </InfoRow>
        )}
        <InfoRow icon={CreditCard} label="Charges">
          <div className="space-y-2">
            <ChargeStageRow
              label="Approval fee ($50)"
              status={appointment.approvalPaymentStatus}
              chargedOnUtc={appointment.approvalChargedOnUtc}
            />
            <ChargeStageRow
              label="Completion charge"
              status={appointment.completionPaymentStatus}
              chargedOnUtc={appointment.completionChargedOnUtc}
            />
          </div>
        </InfoRow>
        {appointment.approvalFeeOutcome !== ApprovalFeeOutcome.None && (
          <InfoRow icon={RotateCcw} label="Approval Fee Outcome">
            <ApprovalFeeOutcomeDetail appointment={appointment} />
          </InfoRow>
        )}
      </CardContent>
    </Card>
  );
}

const OUTCOME_COLOR_CLASSES: Record<string, string> = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-rose-50 text-rose-700 border-rose-200",
  info: "bg-blue-50 text-blue-700 border-blue-200",
  neutral: "bg-muted text-muted-foreground border-border",
};

/**
 * What happened to the $50, and the paper trail for it.
 *
 * `RefundOwed` is the one that needs acting on: our refund did not go through
 * and the patient is still out of pocket, so it is called out rather than shown
 * as one badge among others. There is no button here on purpose. The refund has
 * to be put through in the Stripe dashboard, and the API clears this by itself
 * once Stripe reports it, so a "mark as refunded" control here could only
 * record something untrue.
 */
const ApprovalFeeOutcomeDetail = ({
  appointment,
}: {
  appointment: AppointmentResponse;
}) => {
  const outcome = appointment.approvalFeeOutcome;
  const label = getApprovalFeeOutcomeLabel(outcome);
  const color = getApprovalFeeOutcomeColor(outcome);
  const needsManualRefund = outcome === ApprovalFeeOutcome.RefundOwed;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          className={`text-xs font-medium border ${OUTCOME_COLOR_CLASSES[color]}`}
        >
          {label}
        </Badge>
        {appointment.approvalRefundedCents > 0 && (
          <span className="text-xs text-muted-foreground">
            {formatCents(appointment.approvalRefundedCents)}
            {appointment.approvalRefundedOnUtc
              ? ` on ${formatLongDate(appointment.approvalRefundedOnUtc)}`
              : ""}
          </span>
        )}
      </div>

      {needsManualRefund && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
          <p className="text-xs font-semibold text-rose-800">
            Refund this patient manually in Stripe
          </p>
          {/* No amount here on purpose. A failed refund only records the
              outcome and the reason — `approvalRefundedCents` stays 0 because
              nothing went back — and the fee itself is not on the response, so
              any figure printed here would be a guess at the patient's money.
              The charge in Stripe is the authority. */}
          <p className="mt-1 text-xs text-rose-700">
            The automatic refund did not go through, so the patient is still
            owed the approval fee. Refund the approval charge in Stripe for the
            exact amount.
            {appointment.refundFailureReason
              ? ` Stripe said: ${appointment.refundFailureReason}.`
              : ""}
          </p>
          <p className="mt-1 text-xs text-rose-700">
            This clears itself once Stripe reports the refund; no action is
            needed here afterwards.
          </p>
        </div>
      )}

      {outcome === ApprovalFeeOutcome.Disputed && (
        <p className="text-xs text-rose-700">
          The patient charged this back with their bank.
          {appointment.refundFailureReason
            ? ` ${appointment.refundFailureReason}.`
            : ""}{" "}
          Contest or accept it in the Stripe dashboard.
        </p>
      )}

      {appointment.refundId && (
        <p className="text-[11px] font-mono text-muted-foreground break-all">
          {appointment.refundId}
        </p>
      )}
    </div>
  );
};

const ChargeStageRow = ({
  label,
  status,
  chargedOnUtc,
}: {
  label: string;
  status: AppointmentChargeStatus;
  chargedOnUtc: string | null;
}) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2">
      {status === AppointmentChargeStatus.Paid && chargedOnUtc && (
        <span className="text-xs text-muted-foreground">
          {formatLongDate(chargedOnUtc)}
        </span>
      )}
      <StatusBadge type="charge" status={status} />
    </div>
  </div>
);

export function ProviderInfoCard({
  appointment,
}: {
  appointment: AppointmentResponse;
}) {
  const locationAddress = appointment?.locationId
    ? `${appointment?.locationAddress}, ${appointment?.locationAddress2 || ""} ${!appointment?.locationAddress2 ? "" : ", "}  ${appointment?.locationCity}, ${appointment?.locationState} ${appointment?.locationZip}`
    : "Telehealth";
  return (
    <Card>
      <CardHeader className="p-4 pb-2 lg:p-6 lg:pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Stethoscope className="h-4 w-4 text-muted-foreground" />
          Provider Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0 lg:p-6 lg:pt-0">
        <InfoRow icon={User} label="Specialist">
          <p className="text-sm font-medium text-foreground">
            {appointment.specialistName}
          </p>
        </InfoRow>
        <InfoRow icon={MapPin} label="Practice">
          <p className="text-sm font-medium text-foreground break-words">
            {locationAddress}
          </p>
        </InfoRow>
      </CardContent>
    </Card>
  );
}

export function PatientInfoCard({
  appointment,
}: {
  appointment: AppointmentResponse;
}) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2 lg:p-6 lg:pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <User className="h-4 w-4 text-muted-foreground" />
          Patient Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0 lg:p-6 lg:pt-0">
        <InfoRow icon={User} label="Full Name">
          <p className="text-sm font-medium text-foreground break-words">
            {appointment.userName}
          </p>
        </InfoRow>
        <InfoRow icon={Mail} label="Email">
          <a
            href={`mailto:${appointment.userEmail}`}
            className="text-sm font-medium text-foreground hover:text-primary hover:underline break-all"
          >
            {appointment.userEmail}
          </a>
        </InfoRow>
        <InfoRow icon={Phone} label="Phone">
          {appointment.userPhoneNumber ? (
            <a
              href={`tel:${appointment.userPhoneNumber}`}
              className="text-sm font-medium text-foreground hover:text-primary hover:underline"
            >
              {appointment.userPhoneNumber}
            </a>
          ) : (
            <p className="text-sm font-medium text-muted-foreground">
              Not provided
            </p>
          )}
        </InfoRow>
        <InfoRow icon={Cake} label="Date of Birth">
          {appointment.dateOfBirth ? (
            <p className="text-sm font-medium text-foreground">
              {formatDisplayDate(appointment.dateOfBirth)}
            </p>
          ) : (
            <p className="text-sm font-medium text-muted-foreground">
              Not provided
            </p>
          )}
        </InfoRow>
        <InfoRow icon={CreditCard} label="Medicare">
          {appointment.isMedicare ? (
            <Badge variant="destructive" className="gap-1 text-xs">
              <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
              Medicare
            </Badge>
          ) : (
            <p className="text-sm font-medium text-foreground">No</p>
          )}
        </InfoRow>
        <InfoRow icon={MapPin} label="Searched ZIP">
          {appointment.searchedZipCode ? (
            <p className="text-sm font-medium text-foreground">
              {appointment.searchedZipCode}
            </p>
          ) : (
            <p className="text-sm font-medium text-muted-foreground">
              Not recorded
            </p>
          )}
        </InfoRow>
        <InfoRow icon={MailCheck} label="Email opt-in">
          <p className="text-sm font-medium text-foreground">
            {appointment.emailMarketingConsent ? "✓ Opted in" : "✗ Not opted in"}
          </p>
        </InfoRow>
        <InfoRow icon={MessageSquare} label="SMS opt-in">
          <p className="text-sm font-medium text-foreground">
            {appointment.smsConsent ? "✓ Opted in" : "✗ Not opted in"}
          </p>
        </InfoRow>
      </CardContent>
    </Card>
  );
}

export function ReasonCard({
  appointment,
}: {
  appointment: AppointmentResponse;
}) {
  const hasSymptoms = Boolean(appointment.symptoms?.trim());
  const description = hasSymptoms
    ? appointment.symptoms.trim()
    : "No symptoms provided.";

  return (
    <Card>
      <CardHeader className="p-4 pb-2 lg:p-6 lg:pb-3">
        <CardTitle className="text-base font-semibold">
          Symptom Description
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 lg:p-6 lg:pt-0">
        <p
          className={`text-sm leading-relaxed ${hasSymptoms ? "text-foreground" : "text-muted-foreground"}`}
        >
          {description}
        </p>
      </CardContent>
    </Card>
  );
}
